import {
    ApplicationInsights,
    IEventTelemetry,
    IExceptionTelemetry
} from "@microsoft/applicationinsights-web";

type TrackEventCustomProperties = Parameters<
    ApplicationInsights["trackEvent"]
>[1];

export interface IAppInsightsLoggerConfig {
    instrumentationKey: string;
    authenticatedUserId: string | null;
}

interface IAppInsightsLogger {
    enabled: boolean;
    appInsights: ApplicationInsights | null;
    lastPageViewUrl: string | null;
    init: (config: IAppInsightsLoggerConfig) => void;
    setAuthenticatedUserContext: (username: string | null) => void;
    trackPageView: () => void;
    trackEvent: (
        event: IEventTelemetry,
        customProperties?: TrackEventCustomProperties
    ) => void;
    trackTrace: (name: string, properties?: { [name: string]: string }) => void;
    trackException: (exception: IExceptionTelemetry) => void;
}

export const AppInsightsLogger: IAppInsightsLogger = {
    enabled: false,
    appInsights: null,
    lastPageViewUrl: null,
    init: (config: IAppInsightsLoggerConfig) => {
        if (typeof window !== "undefined") {
            if (window?.appInsights) {
                AppInsightsLogger.appInsights = window.appInsights;
            } else {
                AppInsightsLogger.appInsights = new ApplicationInsights({
                    config: {
                        instrumentationKey: config.instrumentationKey
                        /* ...Other Configuration Options... */
                    }
                });
                AppInsightsLogger.appInsights.loadAppInsights();
            }

            AppInsightsLogger.enabled = true;

            if (config.authenticatedUserId) {
                AppInsightsLogger.setAuthenticatedUserContext(
                    config.authenticatedUserId
                );
            }
            AppInsightsLogger.trackPageView();
        }
    },
    setAuthenticatedUserContext: (username: string | null) => {
        if (AppInsightsLogger.enabled) {
            if (username) {
                AppInsightsLogger.appInsights?.setAuthenticatedUserContext(
                    username
                );
            } else {
                AppInsightsLogger.appInsights?.clearAuthenticatedUserContext();
            }
        }
    },
    trackPageView: () => {
        // title is updated asynchronously so it may not be accurate
        // when a track is triggered
        if (AppInsightsLogger.enabled) {
            const url = window.location.pathname + window.location.search;
            if (url !== AppInsightsLogger.lastPageViewUrl) {
                AppInsightsLogger.lastPageViewUrl = url;

                AppInsightsLogger.appInsights?.trackPageView({
                    name: window.location.pathname,
                    uri: url
                });
            }
        }
    },
    trackEvent: (
        event: IEventTelemetry,
        customProperties?: TrackEventCustomProperties
    ) => {
        if (AppInsightsLogger.enabled) {
            AppInsightsLogger.appInsights?.trackEvent(event, customProperties);
        }
    },
    trackException: (exception: IExceptionTelemetry) => {
        if (AppInsightsLogger.enabled) {
            AppInsightsLogger.appInsights?.trackException(exception);
        }
    },
    trackTrace: (name: string, properties?: { [name: string]: string }) => {
        if (AppInsightsLogger.enabled) {
            AppInsightsLogger.appInsights?.trackTrace({
                message: name,
                properties
            });
        }
    }
};
